import { useState, useEffect, useContext } from 'react';
import { useMutation, useReactiveVar } from '@apollo/client';
import FlyersContext from '@/context/FlyersContext';
import { CallToActionEnum, ReadFlyerResponse, SaveFlyerResponse } from '@/graphql/types';
import { READ_FLYER_MUTATION, SAVE_FLYER_MUTATION } from '@/graphql/mutation';
import { APP_ROUTES, DEFAULT_ERROR_MESSAGE, FlyerType, VariantEnum } from '@/config/constants';
import { cx } from '@emotion/css';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import scrollToTop from '@/utils/scroolToTop';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerStatus } from '@/types/flyer';
import { useNavigationPreviousPage } from '@/hooks/useNavigationPreviousPage';
import {
  FlyerDetailsHeader,
  FlyerDetailsInfo,
  ProvidedByCommunity,
  FlyerDetailsSwiper,
  FlyerDetailsBottom,
  FlyerDetailsStatusModal,
  FlyerDetailsRemoved,
  ExpiredFlyerDetailsInfo
} from './components';
import styles from './FlyerDetails.styles';
import { BodySmall, useSnackbar } from '@/design-system';
import { METRICS_SOURCE_NAME, useFlyer, useMetrics } from '@/hooks';
import { flyerVar, openLoginDrawerVar, userInformationVar } from '@/graphql/state';
import SpinnerOverlay from '@/components/SpinnerOverlay/SpinnerOverlay';
import { FlyerAvailability } from './components/FlyerAvailability/FlyerAvailability';
import { getCTAMetric } from '@/utils/metrictCTAType';
import moment from 'moment';
import { postMetrics } from '@/utils/metricsRequests';
import { SimilarFlyers } from './components/SimilarFlyers';
import AuthenticationContext from '@/context/AuthenticationContext';
import { ShareFlyerDrawer } from '@/components/ShareFlyerDrawer';
import { PrintFlyer } from './components/PrintFlyer';
import config from '@/config/config';
import { PageTitleHelmet } from '@/components';
import colors from '@/theme/colors';

export const FlyerDetails: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { search, state: locationState } = useLocation();
  const audienceId = new URLSearchParams(search).get('audienceId');
  const navigatePreviousPage = useNavigationPreviousPage();
  const [loadingFlyer, setLoadingFlyer] = useState(true);
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState<boolean>(false);


  const extraParams = `?isParentExperience=true${audienceId ? `&audienceId=${audienceId}` : ''}`
  const shareUrl = `${config.REACT_APP_SHARE_HOST}/flyers/${params?.id}/districts/${params?.districtId}${extraParams}`;

  const showToast = useSnackbar();
  const { loading } = useFlyer({
    flyerId: params?.id,
    districtId: params?.districtId,
    schoolId: audienceId
  });
  const flyer = useReactiveVar(flyerVar);
  const user = useReactiveVar(userInformationVar);
  const { isAuthenticated } = useContext(
    AuthenticationContext,
  );
  const openLoginDrawer = useReactiveVar(openLoginDrawerVar);
  const { trackMetrics } = useMetrics();

  const isCommFree = flyer?.type === FlyerType.community;
  const trackId = 'flyer_details_page';
  const flyerStatus = flyer ? flyer?.status : FlyerStatus.removed

  const isNonActive = flyer?.status !== FlyerStatus.active;
  const isExpired = flyer?.status === FlyerStatus.expired;

  const { track } = useAnalytics();
  const [saveFlyer,] = useMutation<SaveFlyerResponse>(SAVE_FLYER_MUTATION);
  const [readFlyer,] = useMutation<ReadFlyerResponse>(READ_FLYER_MUTATION);

  const goToOrgProfile = () => {
    if (flyer?.org) {
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:go_to_org_profile`,
        label: flyer?.org.name,
        value: Number(flyer?.org.orgId),
      });
      navigate(`/org/${flyer.org?.orgId}/profile`);
    }
  };

  const goToExploreFlyers = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:go_to_explore_page`,
      label: 'Go to Explore Page',
    });
    navigate(`/explore`);
  };

  const flyerStates = {
    [FlyerStatus.expired]: {
      title: 'The flyer has expired.',
      variant: VariantEnum.error,
    },
    [FlyerStatus.removed]: {
      title: 'The flyer can not be found.',
      variant: VariantEnum.info,
    },
    [FlyerStatus.pending]: {
      title: 'The flyer is pending approval.',
      variant: VariantEnum.info,
    },
  };

  const updateFlyerAsRead = async() => {
    if (flyer) {
      if (!flyer.isRead) {
        try {
          const { data } = await readFlyer({
            variables: {
              input: {
                flyerId: flyer.flyerId,
                isRead: true,
              },
            },
          });
        }catch(err) {
          showToast({ message: DEFAULT_ERROR_MESSAGE });
        }
      }
    }
  }

  useEffect(() => {
    scrollToTop();
  }, []);

  useEffect(() => {
    if(flyer && loadingFlyer) {
      if (isAuthenticated) {
        updateFlyerAsRead();
      }

      const flyerMetrics = {
        flyerIds: [flyer.flyerId.toString()],
        district: flyer.districtId.toString(),
        school: audienceId,
        eventTime: moment().format(),
        action: 'pa-viewflyer',
        source: METRICS_SOURCE_NAME,
        userLegacyId: user?.id,
      }
      postMetrics({ body: flyerMetrics })
    }
  }, [flyer, loadingFlyer]);

  const showLoginDrawer = () => {
    if(!isAuthenticated && !openLoginDrawer) {
      openLoginDrawerVar(true);
      return true;
    }
    return false;
  }

  const goToPreviousPage = () => {
    if (locationState || isAuthenticated) {
      navigatePreviousPage();
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:back_button`,
        label: flyer?.title,
        value: Number(flyer?.flyerId),
      });
    } else {
      showLoginDrawer();
    }
  };

  const onClickFavorite = async () => {
    if (flyer && !showLoginDrawer()) {
      try {
        const { data } = await saveFlyer({
          variables: {
            input: {
              flyerId: flyer.flyerId,
              isFavorite: !flyer.isFavorite
            },
          },
        });
        if(data?.saveFlyer.flyerId) {
          flyerVar({
            ...flyer,
            isFavorite: !flyer.isFavorite
          });
          const action = flyer.isFavorite ? 'pa-unsave' : 'pa-save';
          trackMetrics({
            flyerId: flyer.flyerId.toString(),
            metric: 'thumbnail-save',
            action,
            resource: 'districts',
            resourceId: flyer?.districtId.toString() ?? '',
            audienceId
          });

          if (!flyer.isFavorite) {
            showToast({
              message: 'The flyer has been saved.',
              buttonLabel: 'View Saved',
              onClick: () => navigate(APP_ROUTES.saved),
              duration: 5000
            })
          } else {
            showToast({
              message: 'Flyer unsaved.',
              duration: 5000
            })
          }
        }
      }catch(err) {
        showToast({ message: DEFAULT_ERROR_MESSAGE });
      }
    }
  };

  const onShare = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:share_flyer`,
      label: flyer?.title,
      value: Number(flyer?.flyerId),
    });
    setIsShareDrawerOpen(true)
  };

  const downloadPDF = (url: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = 'flyer.pdf';
    link.target = '_blank';
    link.dispatchEvent(new MouseEvent('click'));
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:view_flyer`,
      label: flyer?.title,
      value: Number(flyer?.flyerId),
    });
  };

  const onLoadFlyer = () => setLoadingFlyer(false);

  const openLink = ({ origin, value, cta, featureId }: { origin: string, value: string, cta: string, featureId?: string | null }) => {
    trackCTAMetrics(cta, featureId);
    track({
      action: GenericTrackTypes.LINK_PRESSED,
      category: `${trackId}:${origin}:link`,
      label: value,
    });
    switch(cta) {
      case CallToActionEnum.Call: {
        window.location.href = 'tel:' + value;
      }
      break;
      case CallToActionEnum.Email: {
        window.open(`mailto:${value}`);
      }
      break;
      default:
        window.open(value);
    }

  };

  const trackCTAMetrics = (type: string, featureId?: string | null) => {
    const metric = getCTAMetric(type) ?? '';
    trackMetrics(
      {
      flyerId: flyer!.flyerId.toString(),
      metric,
      action: 'pa-cta',
      resource: 'districts',
      resourceId: flyer?.districtId.toString() ?? '',
      featureId: featureId ?? metric,
      audienceId,
    });
  }

  const trackShareOptions = (action: string) => {
    trackMetrics(
      {
      flyerId: flyer!.flyerId.toString(),
      metric: '',
      action,
      resource: 'districts',
      resourceId: flyer?.districtId.toString() ?? '',
      audienceId
    });
  }

  const navigateToViewLocation = () => {
    if(flyer?.mapUrl) {
      navigate(`${APP_ROUTES.maps}?url=${flyer.mapUrl}`);
      trackMetrics(
        {
        flyerId: flyer!.flyerId.toString(),
        metric: "pa-view-location",
        resource: 'districts',
        action: 'pa-view-location',
        resourceId: flyer?.districtId.toString() ?? '',
        audienceId,
      });
    }
  }

  const trackCTAButton = (buttonName: string, link: string, type: string, featureId?: string | null) => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:${buttonName}:cta_button`,
      label: link,
    });
    trackCTAMetrics(type, featureId);
  };

  const addToCalendar = () => {
    if (flyer) {
      track({
        action: GenericTrackTypes.BUTTON_PRESSED,
        category: `${trackId}:add_to_calendar`,
        label: flyer?.title,
        value: flyer?.flyerId,
      });
    }
  };

  const contactClick = (origin: string, email: string) => {
    window.open(`mailto:${email}`, '_self');
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:${origin}:contact`,
      label: flyer?.title,
      value: flyer?.flyerId,
    });
  };

  const slideSwiper = () => {
    if (flyer) {
      track({
        category: `${trackId}:swiper`,
        action: GenericTrackTypes.SLIDE,
        label: flyer?.title,
        value: flyer?.flyerId,
      });
    }
  };

  const renderSkeletons = () => (
    <div
      className={cx(styles.flyerInfoContainer, styles.skeleton)}
      data-testid="skeletonContainer"
    >
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
      <Skeleton variant="text" width="100%" height={40} />
    </div>
  );

  if (loading) return <SpinnerOverlay loading={loading} />;

  return (
    <>
    <PageTitleHelmet>Flyer</PageTitleHelmet>
    {flyer && (
      flyer.status === FlyerStatus.removed ? (
        <FlyerDetailsRemoved
          goToPreviousPage={goToPreviousPage}
          goToExploreFlyers={goToExploreFlyers}
        />
      ) : (
        <>
          <FlyerDetailsHeader
            onShare={onShare}
            isFavorite={flyer.isFavorite}
            goToPreviousPage={goToPreviousPage}
            onClickFavorite={onClickFavorite}
            isNonActive={isNonActive}
            flyerType={flyer.type}
          />

          <FlyerDetailsSwiper
            flyer={flyer}
            loadingFlyer={loadingFlyer}
            isNonActive={isNonActive}
            onLoadFlyer={onLoadFlyer}
            slideSwiper={slideSwiper}
            audienceId={audienceId}
          />

          {loadingFlyer ? (
            renderSkeletons()
          ) : (
            <div>
              <div className={styles.flyerInfoContainer}>
                {isExpired ? (
                  <ExpiredFlyerDetailsInfo
                   flyer={flyer}
                   isCommFree={isCommFree}
                  />
                ) : (
                  <>
                    <FlyerDetailsInfo
                      flyer={flyer}
                      isCommFree={isCommFree}
                      goToOrgProfile={goToOrgProfile}
                      onShare={onShare}
                      downloadPDF={downloadPDF}
                      addToCalendar={addToCalendar}
                      openLink={openLink}
                      emailClick={contactClick}
                      viewLocation={navigateToViewLocation}
                    />
                    {flyer.schools && (
                      <FlyerAvailability
                        schools={flyer.schools}
                        disabled={isNonActive}
                      />
                    )}
                    <SimilarFlyers
                      categories={flyer.categories}
                      flyerType={flyer.type}
                      disabled={isNonActive}
                    />
                  </>
                )}
              </div>
              {/* {isCommFree && flyer.org && (
                <div className={styles.providedBy}>
                  <div className={styles.flex}>
                    <ProvidedByCommunity
                      org={flyer.org}
                      goToOrgProfile={goToOrgProfile}
                      disabled={isNonActive}
                    />
                  </div>
                </div>
              )} */}
              <div className={styles.flyerIdContainer}>
                <BodySmall color={colors.slate}>Flyer ID {flyer.flyerId}</BodySmall>
              </div>
            </div>
          )}
          {!loadingFlyer && !isExpired && (
            <FlyerDetailsBottom
              ctas={flyer.ctas}
              isNonActive={isNonActive}
              trackCTAButton={trackCTAButton}
            />
          )}
           <ShareFlyerDrawer
              open={isShareDrawerOpen}
              onClose={() => setIsShareDrawerOpen(false)}
              thumbnail={flyer.frontImageUrl}
              link={window.location.href}
              pdfUrl={flyer.pdfUrl}
              flyerTitle={flyer.title}
              shareUrl={shareUrl}
              trackMetrics={trackShareOptions}
            />
            <PrintFlyer pages={flyer.pages} />
        </>
        )
      )}

      {flyerStatus !== FlyerStatus.active && (
        <FlyerDetailsStatusModal trackId={trackId} {...flyerStates[flyerStatus]} />
      )}
    </>
  );
};
